/**
 * The Fill panel's slot edits expressed as intents (ADR-009 §3.4, agent
 * parity phase 2). Each builder reproduces the direct command the panel
 * executed, with the same history label; `fill-intents.test.ts` proves byte
 * parity against that command. Filling an image slot decodes a file and
 * stays direct (the decode needs a canvas; see the test).
 */
import type { LayerId } from '@maipo/agent-protocol';
import { changed, noOp, rejected, type CommandExecution } from '../core/commands';
import type { DocumentSession } from '../core/session';
import type { SlotInfo, SlotRole } from '../core/template';
import { dispatchUser } from '../semantic/user-dispatch';
import type { PanelOperation } from './layer-intents';

export function markSlot(layerId: LayerId, name: string, role: SlotRole): PanelOperation {
  return { intents: [{ type: 'slot.mark', layer: layerId, name, role }], label: `Mark slot “${name}”` };
}

export function unmarkSlot(layerId: LayerId): PanelOperation {
  return { intents: [{ type: 'slot.unmark', layer: layerId }], label: 'Remove slot marker' };
}

/** Renaming keeps the role and the saved prompt, as `{ ...slot, name }` did. */
export function renameSlot(layerId: LayerId, name: string): PanelOperation {
  return { intents: [{ type: 'slot.rename', layer: layerId, name }], label: `Mark slot “${name}”` };
}

/** An empty prompt removes the saved prompt. */
export function setSlotPrompt(layerId: LayerId, slot: SlotInfo, prompt: string): PanelOperation {
  return { intents: [{ type: 'slot.rename', layer: layerId, prompt }], label: `Mark slot “${slot.name}”` };
}

/** Fills a text slot with the panel's box growth and anchor shift (FillTextCommand). */
export function fillTextSlot(slotName: string, value: string): PanelOperation {
  return { intents: [{ type: 'slot.fill', slot: slotName, text: value }], label: 'Edit text' };
}

/** Runs an operation as the user and reports it the way the panel's direct commands did. */
export function runFillOperation(session: DocumentSession, operation: PanelOperation): CommandExecution {
  const outcome = dispatchUser(session, operation.intents, operation.label);
  if (outcome.status === 'rejected') return rejected(outcome.message);
  if (outcome.status === 'no-op') return noOp();
  return changed(outcome.command.damage());
}
